import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const VerifyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Listen for auth state changes in case Supabase handles the verification automatically
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state change in VerifyPage:', event, session);
      if (event === 'SIGNED_IN' && session) {
        console.log('User signed in via verification, redirecting to reset password');
        navigate('/reset-password');
      }
    });

    const handleVerification = async () => {
      try {
        // First, check if we already have a session (user might be redirected here after auth)
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          console.log('Session already exists, redirecting to reset password');
          navigate('/reset-password');
          return;
        }

        // Get parameters from both query string and hash fragment
        const token = searchParams.get('token') || new URLSearchParams(window.location.hash.substring(1)).get('access_token');
        const type = searchParams.get('type');
        const redirectTo = searchParams.get('redirect_to');

        console.log('Verification params:', { token, type, redirectTo });
        console.log('Current URL:', window.location.href);
        console.log('Hash fragment:', window.location.hash);

        if (!token || !type) {
          setError('Invalid verification link - missing token or type');
          setLoading(false);
          return;
        }

        if (type === 'recovery') {
          // This is a password reset verification
          // Try different methods to handle the token
          let session = null;
          let error = null;

          // Method 1: Try verifyOtp with token_hash
          try {
            const { data, error: otpError } = await supabase.auth.verifyOtp({
              token_hash: token,
              type: 'recovery'
            });
            session = data.session;
            error = otpError;
          } catch (e) {
            console.log('verifyOtp failed, trying alternative method');
          }

          // Method 2: If verifyOtp failed, try with the token directly
          if (error || !session) {
            try {
              // For recovery type, we need to extract email from the token or use a different approach
              // Let's try using the token as a code for session exchange instead
              const { data, error: tokenError } = await supabase.auth.exchangeCodeForSession(token);
              session = data.session;
              error = tokenError;
            } catch (e) {
              console.log('Direct token verification failed, trying exchangeCodeForSession');
            }
          }

          // Method 3: Try exchangeCodeForSession if we have a code-like token
          if (error || !session) {
            try {
              const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(token);
              session = data.session;
              error = exchangeError;
            } catch (e) {
              console.log('exchangeCodeForSession failed');
            }
          }

          if (error) {
            console.error('Error verifying recovery token:', error);
            setError(`Invalid or expired verification link: ${error.message}`);
            setLoading(false);
            return;
          }

          if (session) {
            // We have a session, redirect to reset password
            console.log('Recovery verification successful, redirecting to reset password');
            navigate('/reset-password');
          } else {
            setError('Unable to verify the recovery link. Please request a new password reset.');
            setLoading(false);
          }
        } else {
          // Other verification types (email confirmation, etc.)
          // For non-recovery types, try exchangeCodeForSession first
          const { data, error } = await supabase.auth.exchangeCodeForSession(token);

          if (error) {
            console.error('Error verifying token:', error);
            setError(`Invalid or expired verification link: ${error.message}`);
            setLoading(false);
            return;
          }

          if (data.session) {
            // Verification successful, redirect to home
            console.log('Verification successful, redirecting to home');
            navigate('/');
          } else {
            setError('Unable to verify the link. Please try again.');
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Error during verification:', error);
        setError(`An error occurred during verification: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setLoading(false);
      }
    };

    handleVerification();

    return () => {
      subscription.unsubscribe();
    };
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying your link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg font-medium text-gray-900 mb-2">Verification Failed</h2>
            <p className="text-sm text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default VerifyPage;
